/**
 * AI Module — ONNX inference + clinical algorithms
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * [ULTIMATE GOD MODE EDITION v10.3 — UNIFIED CORE]
 * 
 * v10.3 REFACTORING:
 * - ✅ Удалены дубликаты функций (estimateEF, tumorKinetics, segmentOrgansByHU, etc.)
 * - ✅ Импорт из aiModuleCore.js (единый источник правды)
 * - ✅ Реэкспорт для обратной совместимости
 * - ✅ BFS fixes (visited при enqueue)
 * - ✅ Zero-GC Tensor Pool в runSegmentation3DLoop
 * - ✅ DICOM SR массив ContentSequence
 * - ✅ _yieldToMain во всех тяжёлых операциях
 */

import { log } from '../utils/logger.js';
import { calciumScore, agatstonWeight, кальцийГруди } from './calciumScoreCore.js';
// [12.0.56] Рантайм ONNX — один хозяин на программу (см. _ensureOrt ниже).
import { ensureOrt as _поднятьOrt, getOrtBackend } from '../rendering/cinematic10/ortLoader.js';

// ══════════════════════════════════════════════════════════════
//  ИМПОРТ ИЗ CORE (единый источник правды)
// ══════════════════════════════════════════════════════════════

import {
  estimateEF,
  tumorKinetics,
  aortaAneurysmScreening,
  segmentOrgansByHU,
  segmentBrainByHU,
  incidentalFindingsScreening,
} from './aiModuleCore.js';

// Реэкспорт для обратной совместимости
export {
  estimateEF,
  tumorKinetics,
  aortaAneurysmScreening,
  segmentOrgansByHU,
  segmentBrainByHU,
  incidentalFindingsScreening,
  agatstonWeight,
};

let ort = null;

const _yieldToMain = () => new Promise(resolve => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(resolve);
  } else {
    setTimeout(resolve, 0);
  }
});

// ══════════════════════════════════════════════════════════════
// ПЕРЕИСПОЛЬЗУЕМЫЕ БУФЕРЫ ПАМЯТИ (Zero-GC Pools)
// ══════════════════════════════════════════════════════════════

let _cclLabelsPool = null;
let _cclQueuePool = null;
let _noduleBfsQueueX = null;
let _noduleBfsQueueY = null;
let _noduleVisitedMarks = null;
let _noduleVisitId = 0;

function _getCCLPools(size) {
  if (!_cclLabelsPool || _cclLabelsPool.length < size) {
    log.debug(`[AI] Allocating CCL pool: ${((size * 4 * 2) / 1048576).toFixed(1)} MB`);
    _cclLabelsPool = new Int32Array(size);
    _cclQueuePool = new Int32Array(size);
  }
  _cclLabelsPool.fill(0);
  return { labels: _cclLabelsPool, queue: _cclQueuePool };
}

function _getNoduleBfsBuffers(sliceArea) {
  if (!_noduleBfsQueueX || _noduleBfsQueueX.length < sliceArea) {
    _noduleBfsQueueX = new Int32Array(sliceArea);
    _noduleBfsQueueY = new Int32Array(sliceArea);
    _noduleVisitedMarks = new Uint32Array(sliceArea);
    _noduleVisitId = 0;
  }
  _noduleVisitId++;
  if (_noduleVisitId >= 4294967290) {
    _noduleVisitedMarks.fill(0);
    _noduleVisitId = 1;
  }
  return {
    queueX: _noduleBfsQueueX,
    queueY: _noduleBfsQueueY,
    visitedMarks: _noduleVisitedMarks,
    visitId: _noduleVisitId
  };
}

// ══════════════════════════════════════════════════════════════
// ONNX RUNTIME INIT & MODEL REGISTRY
// ══════════════════════════════════════════════════════════════

/**
 * [12.0.56] РАНТАЙМ ONNX ПОДНИМАЕТ ОДИН ХОЗЯИН — rendering/cinematic10/ortLoader.js.
 *
 * Здесь был свой загрузчик — копия того, но без выбора движка. «Движок» в
 * настройках вкладки ИИ (WebGPU / WASM) читал только кинорендер: модели ИИ
 * этот выбор не видели и всегда пробовали WebGPU, а надпись обещала
 * «применится при первом запуске ИИ». Теперь рантайм один, выбор читается в
 * одном месте, и исполнителей модели берут по тому же выбору (loadModel).
 */
async function _ensureOrt() {
  if (ort) return ort;
  ort = await _поднятьOrt();
  return ort;
}

const _sessions = new Map();
// [12.0.56] В СПИСКЕ — ТОЛЬКО МОДЕЛИ, КОТОРЫЕ ЗОВЁТ ХОТЬ ОДНА КНОПКА.
// Удалены 'cardiac-chambers' (и её имя 'cardiac-seg') и 'calcium-detect':
// ни одна строка программы их не загружала. «Сердце» считало пороговым
// алгоритмом, даже если файл лежал, — модель числилась «алгоритмическим
// откатом», и путь к ней был заперт; кальций считает calciumScoreCore без
// сети. Из-за них «Проверить модели» писала «Без модели НЕ РАБОТАЕТ:
// calcium-detect» — неправда: не работать было нечему. Удалён и дубль
// 'lung-seg' (тот же файл, что 'lungmask', — имя осталось в _aliasMap).
const _modelUrls = {
  'medsam-encoder': '/models/vision_encoder_quantized.onnx',
  'medsam-decoder': '/models/sam_vit_b_01ec64.decoder.onnx',
  'lungmask': '/models/lungmask_r231.onnx',
  // [11.1.65] Печень целиком за один запуск. livermask (SINTEF, MIT),
  // обучена на LiTS, паренхима 0,946 по Дайсу; переведена в ONNX из
  // model.h5 их выпуска trained-models-v1. Вход [1,1,512,512,1], выход
  // [1,1,512,512,2] — второй канал. Замер владельца на процессоре:
  // 71 мс на срез, то есть около минуты на 818 срезов.
  'liver-seg-net': '/models/liver_unet.onnx',
  // [11.1.79] 117 структур ОДНОЙ моделью, внутри программы, без установки.
  // Dataset298_TotalSegmentator_total_6mm, переведена владельцем в ONNX.
  // Вход [1,1,64,64,64], выход [1,118,64,64,64]. Проверено при переводе:
  // согласие решений с исходной моделью 1.0, расхождение чисел 0.0013.
  // Замер владельца: 512 мс на заплату на процессоре; его том даёт три
  // заплаты, то есть около 1,5 с на все структуры.
  'organs-6mm': '/models/total_6mm.onnx',
  // [11.1.80] Подробная: шаг 3 мм, заплата 112×112×128, свои границы яркости.
  // 54 заплаты против трёх — минуты вместо секунд, зато вдвое подробнее.
  'organs-3mm': '/models/total_3mm.onnx',
  // [11.1.83] ПОЛНАЯ 1,5 мм — пять частей по областям тела. У каждой своя
  // раскладка каналов (25, 27, 19, 24, 27 классов) и своя нормировка, поэтому
  // это пять отдельных моделей, а не одна. Файлы по 119 МБ.
  'organs-15mm-1': '/models/total_15mm_part1.onnx',
  'organs-15mm-2': '/models/total_15mm_part2.onnx',
  'organs-15mm-3': '/models/total_15mm_part3.onnx',
  'organs-15mm-4': '/models/total_15mm_part4.onnx',
  'organs-15mm-5': '/models/total_15mm_part5.onnx',
};

/**
 * [12.0.56] Какая кнопка какой моделью считает — для «Проверить модели».
 * Врачу нужно знать не имя файла, а что перестанет работать без него.
 */
const _кнопкаМодели = {
  'medsam-encoder': '«Обводка сетью MedSAM»',
  'medsam-decoder': '«Обводка сетью MedSAM»',
  lungmask: '«Лёгкие автоматом»',
  'liver-seg-net': '«Печень автоматом»',
  'organs-6mm': '«Органы автоматом», грубая 6 мм',
  'organs-3mm': '«Органы автоматом», подробная 3 мм',
  'organs-15mm-1': '«Органы автоматом», полная 1,5 мм — органы',
  'organs-15mm-2': '«Органы автоматом», полная 1,5 мм — позвонки',
  'organs-15mm-3': '«Органы автоматом», полная 1,5 мм — сердце и сосуды',
  'organs-15mm-4': '«Органы автоматом», полная 1,5 мм — мышцы и кости',
  'organs-15mm-5': '«Органы автоматом», полная 1,5 мм — рёбра',
};

/**
 * [11.1.68] У каких моделей свой список исполнителей и почему.
 *
 * Пусто по умолчанию значит ['webgpu', 'wasm'] — быстрый путь через
 * видеокарту с откатом на процессор. Здесь только исключения, и каждое с
 * причиной: список без причины через полгода никто не решится тронуть.
 */
const _modelProviders = {
  // Сеть печени на трёхмерных свёртках; исполнитель WebGPU их не умеет и
  // отвечает «Only Conv1d and Conv2d are supported». Считаем на процессоре.
  'liver-seg-net': ['wasm'],
  // Та же причина: трёхмерные свёртки, WebGPU их не умеет.
  'organs-6mm': ['wasm'],
  'organs-3mm': ['wasm'],
  // Все пять — тоже на процессоре: трёхмерные свёртки, WebGPU их не умеет.
  'organs-15mm-1': ['wasm'],
  'organs-15mm-2': ['wasm'],
  'organs-15mm-3': ['wasm'],
  'organs-15mm-4': ['wasm'],
  'organs-15mm-5': ['wasm'],
};

// [12.0.56] Имена «сердца» и «кальция» убраны вместе с их файлами (см. выше).
const _aliasMap = {
  'lung-seg': 'lungmask',
};

function _canonicalModelId(modelId) {
  return _aliasMap[modelId] || modelId;
}

// [12.0.56] ОТКАТ БЕЗ СЕТИ — ТОЛЬКО У ЛЁГКИХ, И ОН НАСТОЯЩИЙ.
//
// Прежде здесь стояло одиннадцать имён: «органы», «мозг», «сердце», «RT
// контур», «узлы», «инсульт», «кальций»… Список работал ЗАСОВОМ: модель из
// него не загружалась никогда, даже если файл лежал, — считал пороговый
// «алгоритм». Так lungmask у владельца лежал и не звался ни разу, а карточка
// «Лёгкие» отвечала «не выделены» (искала поле `lungs` среди русских имён).
// Те карточки удалены (надгробие — index.html, вкладка ИИ). Здесь осталось
// одно: без файла lungmask лёгкие берутся по плотности (core/thoraxCore.js,
// тот же путь, что «Эмфизема») — и кнопка говорит, чем посчитано.
const _algorithmicFallbacks = new Set(['lungmask', 'lung-seg']);

export function hasAlgorithmicFallback(modelId) {
  return _algorithmicFallbacks.has(modelId);
}

export async function loadModel(modelId) {
  try {
    modelId = _canonicalModelId(modelId);
    await _ensureOrt();
    if (!ort) { log.warn('[AI] ONNX not loaded'); return null; }
    if (_sessions.has(modelId)) return _sessions.get(modelId);

    const url = _modelUrls[modelId];
    if (!url) throw new Error(`неизвестная модель: ${modelId}`);
    // [12.0.56] Нет файла — так и сказать, с именем файла. Прежде врач получал
    // текст движка («failed to load model…») или ничего.
    if (!await isModelAvailable(modelId)) {
      throw new Error(`нет файла модели ${url.split('/').pop()} в папке models`);
    }

    // [11.1.68] ИСПОЛНИТЕЛИ — ПО МОДЕЛИ, А НЕ ОДИН СПИСОК НА ВСЕХ.
    //
    // Здесь было зашито ['webgpu', 'wasm'] для любой модели. На печени это
    // дало отказ, названный движком дословно: «Only Conv1d and Conv2d are
    // supported» — узел Conv__142_token_2033. Причина не в модели и не в
    // переводе: у ONNX Runtime Web исполнитель WebGPU НЕ УМЕЕТ трёхмерные
    // свёртки, а сеть печени построена на Conv3D (глубина 1, но свёртки
    // трёхмерные — это видно и в форме входа [1,1,512,512,1]).
    //
    // Замер владельца на процессоре давал 71 мс на срез, значит на wasm сеть
    // считается. Поэтому у неё свой список: только wasm, без webgpu вовсе.
    // Порядок в списке — это не «попробуй и упади», а «бери первый годный»:
    // указав webgpu первым, мы получили бы тот же отказ на первом же срезе.
    // [12.0.56] Выбор «Движок: WASM» в настройках ИИ — и для моделей: рантайм
    // поднят без WebGPU, звать его исполнителя незачем.
    const исполнители = _modelProviders[modelId] || (getOrtBackend() === 'wasm' ? ['wasm'] : ['webgpu', 'wasm']);
    log.info(`[AI] загружаю ${modelId}, исполнители: ${исполнители.join(', ')}`);
    const session = await ort.InferenceSession.create(url, {
      executionProviders: исполнители,
      graphOptimizationLevel: 'all',
    });
    _sessions.set(modelId, session);
    return session;
  } catch (e) { log.warn(`[aiModule] loadModel failed:`, e.message); throw e; }
}

/**
 * [11.1.65] СОЗДАТЬ ТЕНЗОР ЧУЖИМ МОДУЛЯМ. Экземпляр ORT живёт только здесь
 * (переменная ort, поднимается _ensureOrt). Клею автосегментации нужен тензор,
 * а поднимать ORT второй раз — значит завести второго хозяина и два движка в
 * памяти. Поэтому даём наружу узкую дверь, а не сам ort.
 *
 * Возвращает null, если движок ещё не поднят: у вызывающего перед этим стоит
 * loadModel, который его и поднимает.
 */
export function создатьТензор(тип, данные, форма) {
  if (!ort) { log.warn('[AI] тензор не создать: ONNX не поднят'); return null; }
  try { return new ort.Tensor(тип, данные, форма); }
  catch (e) { log.warn('[AI] тензор не создался:', e.message); return null; }
}

/**
 * [12.0.56] Прогрев — с отчётом, что прогрето, а чего нет.
 *
 * Прежде ошибки глотались в журнал, а кнопка говорила «✅ Модели готовы» —
 * и без рантайма, и без единого файла. Теперь каждая модель прогревается
 * отдельно (одна неудача не обрывает другие), и ответ перечисляет итог.
 * Прогреваются модели срезовых кнопок: MedSAM (кодировщик) и лёгкие. Печень
 * и органы — тяжёлые трёхмерные сети, их прогрев занял бы память впустую.
 * @returns {Promise<{рантайм:boolean, прогреты:string[], нетФайла:string[], ошибки:string[]}>}
 */
export async function warmupModels() {
  const итог = { рантайм: false, прогреты: [], нетФайла: [], ошибки: [] };
  await _ensureOrt();
  if (!ort) { log.warn('[AI] ONNX не поднят — прогревать нечем'); return итог; }
  итог.рантайм = true;
  const прогреть = async (id, форма) => {
    if (!await isModelAvailable(id)) { итог.нетФайла.push(id); return; }
    try {
      const с = await loadModel(id);
      const n = форма.reduce((a, b) => a * b, 1);
      await с.run({ [с.inputNames[0]]: new ort.Tensor('float32', new Float32Array(n), форма) });
      итог.прогреты.push(id);
    } catch (e) { итог.ошибки.push(`${id}: ${e.message}`); log.warn(`[AI] прогрев ${id}:`, e.message); }
  };
  await прогреть('medsam-encoder', [1, 3, 1024, 1024]);
  await прогреть('lungmask', [1, 1, 256, 256]);
  log.info(`[AI] прогрев: готовы ${итог.прогреты.join(', ') || 'нет'}; нет файла: ${итог.нетФайла.join(', ') || 'нет'}`);
  return итог;
}

/**
 * [10.19.9] Что из моделей реально лежит на месте.
 *
 * Зачем отдельной функцией. Файлы моделей в проект не входят (около 400 МБ),
 * их кладёт врач руками, и до сих пор единственным способом узнать, доехали
 * они или нет, было нажать ИИ-кнопку и посмотреть, не сработал ли молча
 * алгоритмический откат. Теперь можно спросить прямо.
 *
 * Проверяется HEAD-запросом по тому же адресу, по которому модель потом
 * грузится, — то есть проверяется именно то, что будет использовано, а не
 * догадка о путях.
 *
 * @returns {Promise<Array<{модель: string, адрес: string, есть: boolean, мб: number|null, откат: boolean}>>}
 */
export async function проверитьМодели() {
  const итог = [];
  const уникальные = [...new Set(Object.keys(_modelUrls).map(_canonicalModelId))];
  for (const id of уникальные) {
    const адрес = _modelUrls[id];
    if (!адрес) continue;
    let есть = false, мб = null;
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 4000);
      const r = await fetch(`${адрес}?nocache=${Date.now()}`, { method: 'HEAD', signal: ctrl.signal });
      clearTimeout(t);
      есть = r.ok;
      const len = Number(r.headers.get('content-length'));
      if (Number.isFinite(len) && len > 0) мб = Math.round(len / 1048576);
    } catch (e) { есть = false; }
    итог.push({ модель: id, адрес, есть, мб, откат: hasAlgorithmicFallback(id), кнопка: _кнопкаМодели[id] || '' });
  }
  return итог;
}

/**
 * Человеческая сводка по моделям — для кнопки «Проверить модели».
 * [12.0.56] У каждой модели — кнопка, которая ею считает: врачу важно, что
 * перестанет работать, а не имя файла.
 */
export async function сводкаПоМоделям() {
  const список = await проверитьМодели();
  const нашлось = список.filter((м) => м.есть);
  const нет = список.filter((м) => !м.есть);
  const безОтката = нет.filter((м) => !м.откат);
  const файл = (м) => м.адрес.split('/').pop();
  return [
    `Найдено моделей: ${нашлось.length} из ${список.length}.`,
    нашлось.length ? '✅ есть:\n' + нашлось.map((м) => `  ${файл(м)}${м.мб ? ` (${м.мб} МБ)` : ''} — ${м.кнопка}`).join('\n') : '',
    нет.length ? '❌ нет:\n' + нет.map((м) => `  ${файл(м)} — ${м.кнопка}`
      + (м.откат ? ' считает без сети, по плотности' : ' не работает')).join('\n') : '',
    безОтката.length ? '' : 'Всё, что нужно, на месте.',
    нет.length ? 'Файлы кладутся в public/models/ или в корень проекта — сборка забирает оба места.' : '',
  ].filter(Boolean).join('\n');
}

export async function isModelAvailable(modelId) {
  modelId = _canonicalModelId(modelId);
  await _ensureOrt();
  if (!ort || !_modelUrls[modelId]) return false;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 3000);
    const resp = await fetch(`${_modelUrls[modelId]}?nocache=${Date.now()}`, { method: 'HEAD', signal: ctrl.signal });
    clearTimeout(t);
    return resp.ok;
  } catch { return false; }
}

// [12.0.56] УДАЛЕНЫ вместе с карточками вкладки ИИ, которые их звали (надгробие
// карточек — index.html, вкладка ИИ). Ни одна не делала того, что обещала:
//   · runSegmentation, runSegmentation3DLoop — общий прогон сети по срезам
//     без рамки тела, без разворота осей и без чистки; путь лёгких заменён
//     настоящим конвейером lungmask (ai/lungSeg.js, ai/lungMath.js);
//   · samSegmentVolume — «3D SAM» по центру маски соседнего среза; не звался;
//   · advancedLungAnalysis — «пневмония», «саркоидоз/фиброз», «буллы» по
//     порогам HU внутри маски, которой не было (поле lungs не существовало);
//   · classifyStroke — «Ишемический» при разнице средних HU половин кадра
//     больше 3, «Норма 90%» — иначе; без поиска мозга и средней линии;
//   · cirrhosisRiskIndex, bodyCompositionAnalysis — «печень» = 40–80 HU в
//     левой половине кадра, «селезёнка» — в правой, «саркопения» по одному
//     срезу; кнопка к тому же падала TypeError на поле аорты;
//   · detectBodyPart — «HEAD/CHEST/ABDOMEN» по долям воздуха и кости; кнопка
//     читала поля, которых функция не отдаёт: «Unknown (NaN%)»;
//   · autoContourByThreshold — «CTV, PTV, OAR» и «контур» = счёт вокселей
//     −100…200 HU во всём томе; маски не было;
//   · extractMesh — заглушка с пустой сеткой (STL органа — вкладка «Сегментация»).


// ══════════════════════════════════════════════════════════════
// CONNECTED COMPONENTS 3D (26-Связность)
// ══════════════════════════════════════════════════════════════

const _neighborOffsetCache = {};

function _getNeighborOffsets(connectivity) {
  if (_neighborOffsetCache[connectivity]) return _neighborOffsetCache[connectivity];
  
  const dx = [], dy = [], dz = [];
  for (let ddz = -1; ddz <= 1; ddz++) {
    for (let ddy = -1; ddy <= 1; ddy++) {
      for (let ddx = -1; ddx <= 1; ddx++) {
        if (ddx === 0 && ddy === 0 && ddz === 0) continue;
        const absSum = Math.abs(ddx) + Math.abs(ddy) + Math.abs(ddz);
        if (connectivity === 6 && absSum > 1) continue;
        if (connectivity === 18 && absSum > 2) continue;
        dx.push(ddx);
        dy.push(ddy);
        dz.push(ddz);
      }
    }
  }
  
  const offsets = {
    dx: new Int8Array(dx),
    dy: new Int8Array(dy),
    dz: new Int8Array(dz),
    count: dx.length
  };
  
  _neighborOffsetCache[connectivity] = offsets;
  return offsets;
}

async function connectedComponents3D(mask, w, h, d, onProgress, options = {}) {
  const { connectivity = 26 } = options;
  const { labels, queue } = _getCCLPools(mask.length);
  const maxTail = queue.length;
  let nextLabel = 1;
  const sliceSize = w * h;
  const idx = (x, y, z) => z * sliceSize + y * w + x;
  let lastYield = performance.now();
  const totalVoxels = d * w * h;

  const offsets = _getNeighborOffsets(connectivity);

  for (let z = 0; z < d; z++) {
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = idx(x, y, z);

        if ((i & 0x1FFFF) === 0 && performance.now() - lastYield > 15) {
          onProgress?.(Math.round((i / totalVoxels) * 50));
          await _yieldToMain();
          lastYield = performance.now();
        }

        if (!mask[i] || labels[i]) continue;

        labels[i] = nextLabel;
        let head = 0, tail = 0;
        queue[tail++] = i;

        while (head < tail) {
          if (tail >= maxTail) {
            throw new Error(`CCL overflow: lesion exceeds pool size ${maxTail}`);
          }

          const ci = queue[head++];
          const cz = Math.floor(ci / sliceSize);
          const cy = Math.floor((ci % sliceSize) / w);
          const cx = ci % w;

          for (let n = 0; n < offsets.count; n++) {
            const nx = cx + offsets.dx[n];
            const ny = cy + offsets.dy[n];
            const nz = cz + offsets.dz[n];

            if (nx >= 0 && nx < w && ny >= 0 && ny < h && nz >= 0 && nz < d) {
              const ni = nz * sliceSize + ny * w + nx;
              if (mask[ni] && !labels[ni]) {
                labels[ni] = nextLabel;
                queue[tail++] = ni;
              }
            }
          }
        }
        nextLabel++;
      }
    }
  }
  return { labels, count: nextLabel - 1 };
}

// ══════════════════════════════════════════════════════════════
// AGATSTON CALCIUM SCORING (делегирование в core)
// ══════════════════════════════════════════════════════════════

/**
 * Кальциевый индекс — ЕДИНЫЙ вход для всех кнопок (клинические, ИИ,
 * пакетный анализ, viewportAnalysis). [12.0.52] Вся логика — в чистом
 * calciumScoreCore.кальцийГруди (там же — почему так, и проверки на
 * синтетической груди в tests/thorax_test.mjs). Здесь только обёртка.
 * @returns {Promise<{ok:true, totalScore, ...}|{ok:false, отказ:string}>}
 */
export async function calciumScoring(pixels, width, height, depth, spacing, options = {}) {
  try {
    const r = await кальцийГруди(pixels, width, height, depth, spacing, { direction: options.direction });
    options.onProgress?.(100);
    return r;
  } catch (e) { log.warn('[aiModule] calciumScoring failed:', e.message); return { ok: false, отказ: 'счёт сорвался: ' + e.message }; }
}

// ══════════════════════════════════════════════════════════════
// CARDIAC CHAMBERS (O(N) optimization)
// ══════════════════════════════════════════════════════════════

function _getPatientOrientation(orientation) {
  const rowX = orientation[0], rowY = orientation[1], rowZ = orientation[2];
  const colX = orientation[3], colY = orientation[4], colZ = orientation[5];
  
  const normX = rowY * colZ - rowZ * colY;
  const normY = rowZ * colX - rowX * colZ;
  const normZ = rowX * colY - rowY * colX;
  
  const isFlippedX = rowX < -0.01;
  const isAxial = Math.abs(normZ) > 0.9;
  const isCoronal = Math.abs(normY) > 0.9;
  
  return { isFlippedX, isAxial, isCoronal };
}

export async function segmentCardiacChambers(pixels, w, h, d, spacing, options = {}) {
  try {
    const { onProgress, orientation = [1, 0, 0, 0, 1, 0] } = options;
    const total = w * h * d;
    const result = new Uint8Array(total);

    const cx = Math.floor(w / 2), cy = Math.floor(h / 2);
    let centerMaxHU = 0;
    for (let z = Math.floor(d * 0.3); z < Math.floor(d * 0.7); z++) {
      for (let dy_ = -10; dy_ <= 10; dy_++) {
        for (let dx_ = -10; dx_ <= 10; dx_++) {
          const i = z * w * h + (cy + dy_) * w + (cx + dx_);
          if (i >= 0 && i < total && pixels[i] > centerMaxHU) centerMaxHU = pixels[i];
        }
      }
    }

    const hasContrast = centerMaxHU > 200;
    const bloodLow = hasContrast ? 150 : 25;
    const bloodHigh = hasContrast ? 500 : 90;

    const bloodMask = new Uint8Array(total);
    for (let i = 0; i < total; i++) {
      bloodMask[i] = (pixels[i] >= bloodLow && pixels[i] <= bloodHigh) ? 1 : 0;
    }
    onProgress?.(10);

    const { labels, count } = await connectedComponents3D(bloodMask, w, h, d, p => onProgress?.(10 + p * 0.7));
    if (count === 0) { onProgress?.(100); return { mask: result, hasContrast, chambers: {} }; }

    const stats = {};
    for (let i = 1; i <= count; i++) {
      stats[i] = { count: 0, sumX: 0, sumY: 0, sumZ: 0, minZ: d, maxZ: 0 };
    }
    let lastYield = performance.now();

    for (let z = 0; z < d; z++) {
      if (performance.now() - lastYield > 15) { await _yieldToMain(); lastYield = performance.now(); }
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const i = z * w * h + y * w + x;
          const lbl = labels[i];
          if (!lbl) continue;
          const s = stats[lbl];
          s.count++; s.sumX += x; s.sumY += y; s.sumZ += z;
          if (z < s.minZ) s.minZ = z;
          if (z > s.maxZ) s.maxZ = z;
        }
      }
    }

    for (let i = 1; i <= count; i++) {
      const s = stats[i];
      if (s.count === 0) continue;
      s.cx = s.sumX / s.count; s.cy = s.sumY / s.count; s.cz = s.sumZ / s.count;
    }

    const centerZ0 = Math.floor(d * 0.25), centerZ1 = Math.floor(d * 0.75);
    const candidates = Object.entries(stats)
      .filter(([, s]) => s.count > 200 && s.cz >= centerZ0 && s.cz <= centerZ1)
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 4);

    const chambers = {};
    if (candidates.length >= 1) {
      const patientOrientation = _getPatientOrientation(orientation);
      const isFlippedX = patientOrientation.isFlippedX;
      
      const byX = [...candidates].sort((a, b) => 
        isFlippedX ? b[1].cx - a[1].cx : a[1].cx - b[1].cx
      );
      
      const leftTwo = byX.slice(0, Math.ceil(byX.length / 2));
      const rightTwo = byX.slice(Math.ceil(byX.length / 2));
      const leftLow = leftTwo.sort((a, b) => b[1].cz - a[1].cz)[0];
      const leftHigh = leftTwo.sort((a, b) => a[1].cz - b[1].cz)[0];
      const rightLow = rightTwo.sort((a, b) => b[1].cz - a[1].cz)[0];
      const rightHigh = rightTwo.sort((a, b) => a[1].cz - b[1].cz)[0];

      const assignments = [
        [leftLow?.[0], 1, 'LV'], [rightLow?.[0], 2, 'RV'],
        [leftHigh?.[0], 3, 'LA'], [rightHigh?.[0], 4, 'RA'],
      ];

      // O(N) через mapping array
      const labelToChamber = new Uint8Array(count + 1);
      
      for (const [lblStr, labelVal, name] of assignments) {
        if (!lblStr) continue;
        const lbl = parseInt(lblStr);
        labelToChamber[lbl] = labelVal;
        const s = stats[lbl];
        chambers[name] = {
          voxels: s.count,
          volumeMl: Math.round(s.count * spacing[0] * spacing[1] * spacing[2] / 1000 * 10) / 10,
          centroid: [Math.round(s.cx), Math.round(s.cy), Math.round(s.cz)],
        };
      }
      
      for (let i = 0; i < total; i++) {
        const lbl = labels[i];
        if (lbl > 0) result[i] = labelToChamber[lbl];
      }
    }

    onProgress?.(100);
    return { mask: result, hasContrast, chambers };
  } catch (e) { log.warn('[aiModule] segmentCardiacChambers failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// SAM — SEGMENT ANYTHING MODEL
// ══════════════════════════════════════════════════════════════

let _samImageEmbedding = null;
let _samCacheKey = null;

function preprocessForSAM(pixelData, width, height, windowing = { center: 40, width: 400 }) {
  const targetSize = 1024;
  const canvas = new OffscreenCanvas(targetSize, targetSize);
  const ctx = canvas.getContext('2d');
  const low = windowing.center - windowing.width / 2;
  const high = windowing.center + windowing.width / 2;
  const range = windowing.width || 1;

  const normalized = new Uint8ClampedArray(width * height * 4);
  for (let i = 0; i < pixelData.length; i++) {
    const v = Math.round((Math.max(low, Math.min(high, pixelData[i])) - low) / range * 255);
    normalized[i * 4] = v; normalized[i * 4 + 1] = v;
    normalized[i * 4 + 2] = v; normalized[i * 4 + 3] = 255;
  }

  const srcCanvas = new OffscreenCanvas(width, height);
  const srcCtx = srcCanvas.getContext('2d');
  srcCtx.putImageData(new ImageData(normalized, width, height), 0, 0);
  ctx.drawImage(srcCanvas, 0, 0, targetSize, targetSize);

  const imgData = ctx.getImageData(0, 0, targetSize, targetSize);
  const tensor = new Float32Array(3 * targetSize * targetSize);
  const mean = [123.675, 116.28, 103.53];
  const std = [58.395, 57.12, 57.375];

  for (let i = 0; i < targetSize * targetSize; i++) {
    tensor[0 * targetSize * targetSize + i] = (imgData.data[i * 4] - mean[0]) / std[0];
    tensor[1 * targetSize * targetSize + i] = (imgData.data[i * 4 + 1] - mean[1]) / std[1];
    tensor[2 * targetSize * targetSize + i] = (imgData.data[i * 4 + 2] - mean[2]) / std[2];
  }
  return { tensor, scaleX: targetSize / width, scaleY: targetSize / height };
}

export async function samEncodeImage(pixelData, width, height, imageId, windowing = { center: 40, width: 400 }) {
  try {
    const cacheKey = `${imageId}_${windowing.center}_${windowing.width}`;
    if (imageId && cacheKey === _samCacheKey && _samImageEmbedding) return _samImageEmbedding;

    const session = await loadModel('medsam-encoder');
    const { tensor, scaleX, scaleY } = preprocessForSAM(pixelData, width, height, windowing);
    const inputTensor = new ort.Tensor('float32', tensor, [1, 3, 1024, 1024]);
    const result = await session.run({ [session.inputNames[0]]: inputTensor });

    _samImageEmbedding = { embedding: result[session.outputNames[0]], scaleX, scaleY, width, height };
    _samCacheKey = cacheKey;
    return _samImageEmbedding;
  } catch (e) { log.warn('[aiModule] samEncodeImage failed:', e.message); return null; }
}

export async function samDecodePoints(points, embedding) {
  try {
    const session = await loadModel('medsam-decoder');
    const pointCoords = new Float32Array(points.length * 2);
    const pointLabels = new Float32Array(points.length);

    for (let i = 0; i < points.length; i++) {
      pointCoords[i * 2] = points[i].x * embedding.scaleX;
      pointCoords[i * 2 + 1] = points[i].y * embedding.scaleY;
      pointLabels[i] = points[i].label;
    }

    const feeds = {
      image_embeddings: embedding.embedding,
      point_coords: new ort.Tensor('float32', pointCoords, [1, points.length, 2]),
      point_labels: new ort.Tensor('float32', pointLabels, [1, points.length]),
      mask_input: new ort.Tensor('float32', new Float32Array(256 * 256), [1, 1, 256, 256]),
      has_mask_input: new ort.Tensor('float32', new Float32Array([0]), [1]),
      orig_im_size: new ort.Tensor('float32', new Float32Array([1024, 1024]), [2]),
    };

    const result = await session.run(feeds);
    const выходМасок = result[session.outputNames[0]];
    const masks = выходМасок.data;
    const ious = result[session.outputNames[1]].data;

    // [11.1.60] ФОРМА БЕРЁТСЯ У ТЕНЗОРА, А НЕ УГАДЫВАЕТСЯ.
    //
    // Здесь стояло: maskSize = masks.length / 3 (считая, что модель вернула
    // ТРИ маски), а размер — Math.sqrt(maskSize). Владелец увидел в окне
    // результата «Размер маски 591 × 591» при оригинале 512×512. Обратный
    // счёт: одна маска 1024×1024 — это 1 048 576 чисел, делённые на три дают
    // 349 525, а корень из них равен 591,2. То есть его модель возвращает ОДНУ
    // маску, а мы брали ТРЕТЬ ответа и растолковывали её как квадрат со
    // стороной 591. На холст пошла бы полоса мусора вместо печени — при
    // честном IoU 0,93 и полной видимости «успеха».
    //
    // Угадывать нечего: тензор знает свою форму. dims у SAM-декодера —
    // [1, число_масок, высота, ширина]. Берём оттуда, а на случай иной формы
    // остаётся прежний путь, но с ЧЕСТНЫМ числом масок из ious.
    const d = выходМасок.dims || [];
    let числоМасок, высота, ширина;
    if (d.length === 4) {
      [, числоМасок, высота, ширина] = d;
    } else {
      числоМасок = Math.max(1, ious?.length || 1);
      const наМаску = Math.round(masks.length / числоМасок);
      высота = ширина = Math.round(Math.sqrt(наМаску));
      log.warn(`[SAM] декодер отдал форму [${d.join('×') || '?'}] — размер восстановлен счётом: `
        + `${числоМасок} масок по ${ширина}×${высота}`);
    }
    const maskSize = высота * ширина;
    const bestIdx = Math.min(
      Math.max(0, Array.from(ious).indexOf(Math.max(...ious))),
      Math.max(0, числоМасок - 1),
    );
    if (masks.length < (bestIdx + 1) * maskSize) {
      log.warn(`[SAM] ответ декодера короче ожидаемого: ${masks.length} чисел, `
        + `нужно ${(bestIdx + 1) * maskSize} для маски ${ширина}×${высота}`);
      return {};
    }
    const bestMask = masks.slice(bestIdx * maskSize, (bestIdx + 1) * maskSize);
    log.info(`[SAM] маска ${ширина}×${высота}, масок в ответе ${числоМасок}, выбрана ${bestIdx + 1}, `
      + `IoU ${Math.max(...ious).toFixed(3)}`);

    const binary = new Uint8Array(bestMask.length);
    for (let i = 0; i < bestMask.length; i++) binary[i] = bestMask[i] > 0 ? 1 : 0;

    return {
      mask: binary,
      maskWidth: ширина,
      maskHeight: высота,
      iou: Math.max(...ious),
      origWidth: embedding.width,
      origHeight: embedding.height,
    };
  } catch (e) { log.warn('[aiModule] samDecodePoints failed:', e.message); return {}; }
}


export function clearSamCache() { _samImageEmbedding = null; _samCacheKey = null; }

// ══════════════════════════════════════════════════════════════
// SMART REGION GROWING (FIXED BFS)
// ══════════════════════════════════════════════════════════════

export async function smartRegionGrowing(pixels, w, h, d, startX, startY, startZ, tolerance = 50, gradientThreshold = 100) {
  try {
    const total = w * h * d;
    const sliceSize = w * h;
    const mask = new Uint8Array(total);
    const queue = new Int32Array(total);
    const idx = (x, y, z) => z * sliceSize + y * w + x;
    const startIdx = startZ * sliceSize + startY * w + startX;
    const startHU = pixels[startIdx];

    let head = 0, tail = 0;
    
    // Помечаем visited ДО enqueue
    mask[startIdx] = 1;
    queue[tail++] = startIdx;
    
    let voxelsCount = 1;
    let lastYield = performance.now();

    while (head < tail && voxelsCount < 500000) {
      if (performance.now() - lastYield > 15) { await _yieldToMain(); lastYield = performance.now(); }

      const curr = queue[head++];
      const cz = Math.floor(curr / sliceSize);
      const cy = Math.floor((curr % sliceSize) / w);
      const cx = curr % w;
      const currHU = pixels[curr];

      const neighbors = [
        cx > 0 ? idx(cx - 1, cy, cz) : -1, cx < w - 1 ? idx(cx + 1, cy, cz) : -1,
        cy > 0 ? idx(cx, cy - 1, cz) : -1, cy < h - 1 ? idx(cx, cy + 1, cz) : -1,
        cz > 0 ? idx(cx, cy, cz - 1) : -1, cz < d - 1 ? idx(cx, cy, cz + 1) : -1
      ];

      for (const ni of neighbors) {
        if (ni >= 0 && mask[ni] === 0) {
          const niHU = pixels[ni];
          if (Math.abs(niHU - startHU) <= tolerance && Math.abs(niHU - currHU) <= gradientThreshold) {
            mask[ni] = 1;
            queue[tail++] = ni;
            voxelsCount++;
          } else {
            mask[ni] = 2;
          }
        }
      }
    }

    for (let i = 0; i < total; i++) if (mask[i] === 2) mask[i] = 0;
    return { mask, voxelsCount };
  } catch (e) { log.warn('[aiModule] smartRegionGrowing failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// LUNG NODULES DETECTION (FIXED BFS)
// ══════════════════════════════════════════════════════════════

export async function detectLungNodules(pixels, w, h, d, spacing, options = {}) {
  try {
    const { threshold = 0.7, onProgress } = options;
    const sliceSize = w * h;
    const nodules = [];
    let lastYield = performance.now();
    
    const { queueX, queueY, visitedMarks, visitId } = _getNoduleBfsBuffers(sliceSize);

    for (let z = 2; z < d - 2; z += 3) {
      if (performance.now() - lastYield > 15) {
        onProgress?.(Math.round((z / d) * 100));
        await _yieldToMain();
        lastYield = performance.now();
      }

      for (let y = 10; y < h - 10; y += 5) {
        for (let x = 10; x < w - 10; x += 5) {
          const idx = z * sliceSize + y * w + x;
          const hu = pixels[idx];
          if (hu < -200 || hu > 150) continue;

          let lungNeighbors = 0, totalNeighbors = 0;
          for (let dy = -5; dy <= 5; dy += 5) {
            for (let dx = -5; dx <= 5; dx += 5) {
              if (dx === 0 && dy === 0) continue;
              totalNeighbors++;
              const nhu = pixels[idx + dy * w + dx];
              if (nhu >= -950 && nhu <= -500) lungNeighbors++;
            }
          }

          if (totalNeighbors > 0 && lungNeighbors / totalNeighbors >= threshold) {
            let size = 0, sumHU = 0;
            let head = 0, tail = 0;
            
            const startVk = y * w + x;
            
            // Помечаем visited ДО enqueue
            visitedMarks[startVk] = visitId;
            queueX[tail] = x;
            queueY[tail] = y;
            tail++;

            while (head < tail && size < 2000) {
              const cx = queueX[head];
              const cy = queueY[head];
              head++;

              const vk = cy * w + cx;
              const ci = z * sliceSize + vk;
              if (ci < 0 || ci >= pixels.length) continue;
              const chu = pixels[ci];
              if (chu < -200 || chu > 150) continue;

              size++; sumHU += chu;
              
              if (cx > 0) {
                const nvk = cy * w + (cx - 1);
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx - 1; queueY[tail] = cy; tail++;
                }
              }
              if (cx < w - 1) {
                const nvk = cy * w + (cx + 1);
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx + 1; queueY[tail] = cy; tail++;
                }
              }
              if (cy > 0) {
                const nvk = (cy - 1) * w + cx;
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx; queueY[tail] = cy - 1; tail++;
                }
              }
              if (cy < h - 1) {
                const nvk = (cy + 1) * w + cx;
                if (visitedMarks[nvk] !== visitId) {
                  visitedMarks[nvk] = visitId;
                  queueX[tail] = cx; queueY[tail] = cy + 1; tail++;
                }
              }
            }

            if (size >= 5 && size < 1500) {
              const areaMm2 = size * spacing[0] * spacing[1];
              const diamMm = parseFloat(Math.sqrt(areaMm2 * 4 / Math.PI).toFixed(1));
              const meanHU = Math.round(sumHU / size);

              let perimeter = 0;
              for (let q = 0; q < head; q++) {
                const px = queueX[q];
                const py = queueY[q];
                if (px === 0 || visitedMarks[py * w + (px - 1)] !== visitId) perimeter++;
                if (px >= w - 1 || visitedMarks[py * w + (px + 1)] !== visitId) perimeter++;
                if (py === 0 || visitedMarks[(py - 1) * w + px] !== visitId) perimeter++;
                if (py >= h - 1 || visitedMarks[(py + 1) * w + px] !== visitId) perimeter++;
              }

              const circularity = (4 * Math.PI * size) / (perimeter * perimeter || 1);
              if (circularity < 0.4) continue;

              let lungRADS;
              if (diamMm < 4) lungRADS = 'Lung-RADS 2 (доброкачественный)';
              else if (diamMm < 6) lungRADS = 'Lung-RADS 3 (вероятно доброкачественный)';
              else if (diamMm < 8) lungRADS = 'Lung-RADS 4A (подозрительный)';
              else lungRADS = 'Lung-RADS 4B (очень подозрительный)';

              const isDupe = nodules.some(n => Math.abs(n.x - x) < 15 && Math.abs(n.y - y) < 15 && Math.abs(n.z - z) < 5);
              if (!isDupe) nodules.push({ x, y, z, voxels: size, diameterMm: diamMm, meanHU, lungRADS });
            }
          }
        }
      }
    }

    nodules.sort((a, b) => b.diameterMm - a.diameterMm);
    onProgress?.(100);
    return { totalFound: nodules.length, nodules: nodules.slice(0, 20) };
  } catch (e) { log.warn('[aiModule] detectLungNodules failed:', e.message); return {}; }
}

// ══════════════════════════════════════════════════════════════
// LIFECYCLE & DIAGNOSTICS
// ══════════════════════════════════════════════════════════════

/** @returns {Promise<number>} сколько моделей было выгружено [12.0.56] */
export async function releaseModels() {
  const было = _sessions.size;
  try {
    for (const session of _sessions.values()) {
      if (session.release) await session.release();
    }
    _sessions.clear();
    clearSamCache();

    _cclLabelsPool = null;
    _cclQueuePool = null;
    _noduleBfsQueueX = null;
    _noduleBfsQueueY = null;
    _noduleVisitedMarks = null;
    _noduleVisitId = 0;

    log.info(`[AI] выгружено моделей: ${было}`);
  } catch (e) { log.warn('[aiModule] releaseModels failed:', e.message); }
  return было;
}

export function getAIModuleState() {
  return {
    ortLoaded: !!ort,
    loadedModels: [..._sessions.keys()],
    loadedModelCount: _sessions.size,
    availableModels: Object.keys(_modelUrls),
    availableModelCount: Object.keys(_modelUrls).length,
    aliases: { ..._aliasMap },
    algorithmicFallbacks: [..._algorithmicFallbacks],
    memoryPoolAllocated: !!_cclLabelsPool,
    memoryPoolSizeMB: _cclLabelsPool ? +((_cclLabelsPool.byteLength * 2) / 1048576).toFixed(1) : 0,
  };
}

export function listAvailableModels() {
  return Object.entries(_modelUrls).map(([id, url]) => ({
    id, url, loaded: _sessions.has(id), hasAlgorithmicFallback: _algorithmicFallbacks.has(id),
  }));
}

// [12.0.56] Список приведён к тому, что в модуле есть на деле.
export function listAICapabilities() {
  return [
    'calciumScoring', 'samSegmentation', 'lungmask',
    'lungNoduleDetection', 'tumorKinetics',
    'aortaAneurysmScreening', 'incidentalFindingsScreening',
  ];
}

export function getModelUrl(modelId) { return _modelUrls[_canonicalModelId(modelId)] || null; }
export function isOrtLoaded() { return !!ort; }

export { connectedComponents3D, _getCCLPools };